import { CohortStepStatus, CohortStepType } from 'src/types/cohorts';
import { motion } from 'motion/react';
import { MaterialSymbol } from 'react-material-symbols';
import { CreateCohortButton } from '../create-cohort-button';

export interface ResultStepProps {
  step: {
    data: {
      type: string;
      payload: {
        content: string;
        format: string;
        metrics?: any[];
        summary?: string;
        headers?: string[];
        rows?: any[][];
      };
    }[];
    type: CohortStepType;
    status: CohortStepStatus;
  };
  isActive: boolean;
  isCompleted: boolean;
  onCreateCohort: () => void;
}

export const ResultStep = ({ step, isActive, isCompleted, onCreateCohort }: ResultStepProps) => {
  const renderMetrics = (content: any) => {
    if (!content.payload.metrics) return null;

    return (
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {content.payload.metrics.map((metric: any, idx: number) => (
          <div key={idx} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <p className="text-gray-400 text-sm">{metric.title}</p>
            <p className="text-white text-2xl font-bold mt-2">{metric.value}</p>
            {metric.change !== undefined && (
              <div
                className={`flex items-center mt-2 ${
                  metric.change > 0 ? 'text-green-400' : 'text-red-400'
                }`}
              >
                <MaterialSymbol
                  icon={metric.change > 0 ? 'trending_up' : 'trending_down'}
                  className="w-4 h-4 mr-1"
                />
                <span className="text-sm">{metric.change}%</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSummary = (content: any) => {
    if (!content.payload.summary) return null;

    return (
      <div className="mt-6 bg-gray-800 rounded-lg p-6 border-l-4 border-cyan-500">
        <h4 className="text-white font-medium mb-2">Summary</h4>
        <p className="text-gray-300">{content.payload.summary}</p>
      </div>
    );
  };

  const renderTable = (content: any) => {
    if (!content.payload.headers || !content.payload.rows) return null;

    return (
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-700 border border-gray-700 rounded">
          <thead className="bg-gray-800">
            <tr>
              {content.payload.headers.map((header: string, idx: number) => (
                <th
                  key={idx}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-gray-900 divide-y divide-gray-700">
            {content.payload.rows.map((row: any[], rowIndex: number) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGraph = (content: any) => {
    return (
      <div className="mt-4 bg-gray-800 rounded-lg p-6">
        <h4 className="text-white font-medium mb-4">Graph Visualization</h4>
        <div className="bg-gray-700 h-64 rounded flex items-center justify-center">
          <p className="text-gray-400">Graph visualization would be rendered here</p>
        </div>
        {content.payload.description && (
          <p className="text-gray-300 text-sm mt-4">{content.payload.description}</p>
        )}
      </div>
    );
  };

  return (
    <motion.div
      className={`mb-6 ${isActive ? '' : 'opacity-90'}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0.7 }}
      transition={{ duration: 0.5 }}
    >
      {/* Render different content types */}
      {step.data.map((content, contentIndex) => (
        <motion.div
          key={contentIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + contentIndex * 0.1, duration: 0.4 }}
        >
          {content.type === 'text' && (
            <div className="prose prose-invert max-w-none">{content.payload.content}</div>
          )}
          {content.type === 'metric' && renderMetrics(content)}
          {content.type === 'table' && renderTable(content)}
          {content.type === 'graph' && renderGraph(content)}
          {content.payload.summary && renderSummary(content)}
        </motion.div>
      ))}

      {/* Show create cohort button when appropriate */}
      {isActive && isCompleted && (
        <div className="mt-6">
          <CreateCohortButton onClick={onCreateCohort} />
        </div>
      )}
    </motion.div>
  );
};
